'use client';

import { useCallback } from 'react';
import { TransactionInstruction } from '@solana/web3.js';
import { convertLeverageToMarginRatio } from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import type { Adapter } from '@solana/wallet-adapter-base';
import invariant from 'tiny-invariant';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';

export interface SubaccountSettingsUpdate {
	maxLeverage?: number; // Displayed as "Max Leverage"
	marginTradingEnabled?: boolean; // Spot margin enabled
	reduceOnly?: boolean;
}

/**
 * Hook for updating subaccount settings like leverage, margin trading, and
 * reduce-only.
 */
const useUpdateSubaccountSettings = () => {
	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const getTxParams = useGetTxParams();

	const updateSettingsAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			updates: Record<number, SubaccountSettingsUpdate>
		) => {
			invariant(velocityClient, 'driftClient is required');

			const ixs: TransactionInstruction[] = [];

			await Promise.all(
				Object.keys(updates).map(async (key) => {
					const subAccountId = Number(key);
					// Must check if the key exists here because the value of maxLeverage will be set to undefined if the user is changing it to "no limit"
					if (Object.keys(updates[subAccountId]).includes('maxLeverage')) {
						const marginRatio = convertLeverageToMarginRatio(
							updates[subAccountId].maxLeverage as number
						);
						ixs.push(
							(await velocityClient.getUpdateUserCustomMarginRatioIx(
								marginRatio as number,
								subAccountId as number
							)) as TransactionInstruction
						);
					}
					if (
						Object.keys(updates[subAccountId]).includes('marginTradingEnabled')
					) {
						ixs.push(
							(await velocityClient.getUpdateUserMarginTradingEnabledIx(
								updates[subAccountId].marginTradingEnabled as boolean,
								subAccountId
							)) as TransactionInstruction
						);
					}
					if (Object.keys(updates[subAccountId]).includes('reduceOnly')) {
						ixs.push(
							(await velocityClient.getUpdateUserReduceOnlyIx(
								updates[subAccountId].reduceOnly as boolean,
								subAccountId
							)) as TransactionInstruction
						);
					}
				})
			);

			if (ixs.length === 0) {
				throw new Error('No instructions to update subaccount settings');
			}

			await wallet.signAndSendTransaction(ixs, getTxParams());
		},
		[velocityClient, getTxParams]
	);

	const wrappedUpdateSettings =
		useSponsoredSignVelocityActionWithFallback(updateSettingsAction);

	/**
	 * Updates all subaccount flags / margin settings in one transaction
	 */
	const updateSubaccountSettings = useCallback(
		async (
			updates: Record<number, SubaccountSettingsUpdate>
		): Promise<boolean> => {
			const toastId = `updatingSettings_${Object.keys(updates).join(',')}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedUpdateSettings(updates);

				notify({
					id: toastId,
					type: 'success',
					message: 'Updated margin settings.',
				});
				return true;
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as Adapter,
				});
				return false;
			}
		},
		[wrappedUpdateSettings, walletAdapter]
	);

	return {
		updateSubaccountSettings,
	};
};

export default useUpdateSubaccountSettings;
